import type { ReportPeriod, SentimentReportQuery } from '@discovr/types';

/**
 * Canonical React Query keys for the campaign-detail surface.
 * All fetch queries and all SSE invalidations use these — single source of truth.
 * Keep tuples `as const` so TS infers literal element types — required
 * for QueryKey type narrowing in React Query v5.
 */

/**
 * Shared tab IDs for the campaign-detail page. Both admin and brand use
 * these core tabs; each app extends with role-specific extras:
 *   admin: 'brief'
 *   brand: 'overview' | 'live'
 */
export type CampaignTabId =
  | 'details'
  | 'reporting'
  | 'creators'
  | 'confirmed'
  | 'scripts'
  | 'content'
  | 'live-content';

export type RosterTab =
  | 'creators'
  | 'rejected'
  | 'scripts'
  | 'content'
  | 'finalized'
  | 'live-content';

export type ThreadType = 'selection' | 'script' | 'content';

/** Server-side sort params, shared by the roster query key (RosterFilters) and
 *  the list-endpoint params (UnifiedListParams) so the two can't drift apart. */
export interface SortParams {
  /** Active column sort field (a roster column id, e.g. followers / est_cpv).
   *  Part of the cache key so each sort is a distinct query (re-fetched from page
   *  1), never a client re-slice of a differently-sorted page. */
  sort_by?: string;
  /** Sort direction. Absent lets the server pick its per-field default. */
  sort_dir?: 'asc' | 'desc';
}

export interface RosterFilters extends SortParams {
  status?: string;
  page?: number;
  limit?: number;
  /** Client-side-only bucket discriminator for query keys whose BE endpoint
   *  has no status filter (e.g. roster/finalized) — gives each pill its own
   *  cache slot despite every pill hitting the identical underlying request.
   *  Never sent to the BE (unifiedRosterQs doesn't read it). */
  phase?: string;
  /** Search term — part of the cache key so each query is cached per search. */
  search?: string;
}

/** The page, size and sort a Creator Content request carries; each combination is its own cache entry. */
export interface CreatorContentParams {
  page: number;
  limit: number;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  /** Trimmed, non-empty creator search; absent when not searching. */
  search?: string;
}

const reportsPrefix = (id: string) => ['campaign-report', id] as const;
const DEFAULT_PERIOD_KEY = 'default';
export const SENTIMENT_CREATOR_PAGE_SIZE = 50;

export const campaignKeys = {
  all: ['campaign'] as const,
  detail: (id: string) => ['campaign', 'detail', id] as const,
  editRequestCurrent: (id: string) => ['campaign', 'edit-request', 'current', id] as const,
  editRequestHistory: (id: string) => ['campaign', 'edit-request', 'history', id] as const,

  rosterAll: (id: string) => ['roster', id] as const,
  rosterTab: (id: string, tab: RosterTab, filters?: RosterFilters) =>
    ['roster', id, tab, filters ?? {}] as const,

  /** Tab-aggregated KPI keys served by /roster-kpis/{tab}. Separate from
   *  the tab list query so a list page-change doesn't refetch the KPI
   *  and an SSE-driven KPI refresh doesn't refetch the list. */
  rosterKpisCreators: (id: string) => ['roster-kpis', id, 'creators'] as const,
  rosterKpisFinalized: (id: string) => ['roster-kpis', id, 'finalized'] as const,

  /** ['roster', id, 'counts'] — consolidated per-status + per-phase counts.
   *  Sits under the ['roster', id] prefix so the existing SSE rosterPredicate
   *  auto-invalidates it alongside the tab lists. */
  rosterCounts: (id: string) => ['roster', id, 'counts'] as const,

  conversation: (entryId: string, type: ThreadType) =>
    ['conversation', entryId, type] as const,

  /** The full, unpaginated anchor set for one script version — see
   *  useAnchoredMessages. Keyed by versionId (not just entryId/type) so a
   *  version switch is a fresh query, not a stale cache hit. */
  anchoredMessages: (entryId: string, type: ThreadType, versionId: string) =>
    ['conversation', entryId, type, 'anchors', versionId] as const,

  scriptDetail: (entryId: string) => ['roster', entryId, 'script-detail'] as const,
  contentDetail: (entryId: string) => ['roster', entryId, 'content-detail'] as const,
  contentVideoUrl: (entryId: string) => ['roster', entryId, 'content-video-url'] as const,
  creatorAnalytics: (entryId: string) =>
    ['roster', entryId, 'analytics-metrics'] as const,

  /** ['campaign-last-import', id] — used by useLastImportResult + SSE setQueryData. */
  lastImport: (campaignId: string) => ['campaign-last-import', campaignId] as const,

  /** ['campaigns-list', page, limit] — specific page; prefix ['campaigns-list'] invalidates all. */
  campaignList: (page: number, limit: number) => ['campaigns-list', page, limit] as const,
  /** Prefix key — invalidates every page of the campaign list. */
  campaignListAll: () => ['campaigns-list'] as const,

  /** ['campaigns-list','all'] — the fetch-all (whole-dataset) query for
   *  client-side search/pagination. Sits UNDER the ['campaigns-list'] prefix so
   *  campaignListAll() invalidation already covers it (no extra effect needed). */
  campaignListFull: () => ['campaigns-list', 'all'] as const,

  /** ['roster-resolve-creator', campaignId, username] — resolve which tab/pill/page
   *  a creator lands on in a given campaign. Used by the shareable-link relanding hook. */
  resolveCreator: (campaignId: string, username: string) =>
    ['roster-resolve-creator', campaignId, username] as const,

  /** ['campaign', 'share-grants', campaignId] — authed share-grant list for one
   *  campaign (mint/list/revoke). Invalidated by useMintShareGrant/useRevokeShareGrant. */
  shareGrants: (id: string) => ['campaign', 'share-grants', id] as const,

  /** ['campaign', 'link-funnel-summary', campaignId, window] — campaign
   *  share-link funnel summary (device split, public/private split,
   *  drop-off, conversions). */
  linkFunnelSummary: (id: string, window?: string) => ['campaign', 'link-funnel-summary', id, window ?? ''] as const,

  /** Reporting tabs' finished data, all under ['campaign-report', id] — a prefix of its own,
   *  so a roster/KPI invalidation never refetches a report it does not name. */
  reports: (id: string) => reportsPrefix(id),
  generalReports: (id: string) => [...reportsPrefix(id), 'general'] as const,
  generalReport: (id: string, period?: ReportPeriod) =>
    [...reportsPrefix(id), 'general', period ?? DEFAULT_PERIOD_KEY] as const,
  sentimentReports: (id: string) => [...reportsPrefix(id), 'sentiment'] as const,
  sentimentReport: (id: string, q: SentimentReportQuery) =>
    [
      ...reportsPrefix(id),
      'sentiment',
      q.from ?? 'start',
      q.to ?? 'today',
      q.creatorLimit ?? SENTIMENT_CREATOR_PAGE_SIZE,
      q.creatorOffset ?? 0,
    ] as const,
  audienceReport: (id: string) => [...reportsPrefix(id), 'audience'] as const,
  creatorContentReports: (id: string) => [...reportsPrefix(id), 'creator-content'] as const,
  creatorContentReport: (id: string, params: CreatorContentParams) =>
    [...reportsPrefix(id), 'creator-content', params] as const,

  /** ['campaign-report', id, 'exports'] — the export history list; under the report prefix so a reconnect refresh covers it. */
  reportExports: (id: string) => [...reportsPrefix(id), 'exports'] as const,

  /** ['campaign', 'report-export', id] — MUTATION key of the Excel export. Lets any mounted
   *  button (including one remounted mid-flight) see that an export is already running. */
  reportExport: (id: string) => ['campaign', 'report-export', id] as const,
} as const;

